import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import Message from "./components/Message";
import Loading from "./components/Loading";
import { Theme } from "./constants/theme";

const UIContext = createContext();
const THEME_STORAGE_KEY = 'portal_theme';

const MIN_LOADING_MS = 250;
const MAX_IMAGE_WAIT_MS = 5000;

function getSystemTheme() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return Theme.LIGHT;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? Theme.DARK : Theme.LIGHT;
}

function getEffectiveTheme(userTheme, systemTheme) {
  if (userTheme === Theme.SYSTEM) {
    return systemTheme;
  }

  if (userTheme === Theme.DARK || userTheme === Theme.LIGHT) {
    return userTheme;
  }

  return systemTheme;
}

function getStoredTheme() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    if (storedTheme === Theme.LIGHT || storedTheme === Theme.DARK || storedTheme === Theme.SYSTEM) {
      return storedTheme;
    }
  } catch {
    // Ignore storage failures and use defaults.
  }

  return undefined;
}

function waitForNextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

function waitForImages() {
  const images = Array.from(document.images || []).filter((img) => !img.complete);

  if (images.length === 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let pending = images.length;

    const onDone = () => {
      pending -= 1;
      if (pending <= 0) {
        resolve();
      }
    };

    images.forEach((img) => {
      img.addEventListener("load", onDone, { once: true });
      img.addEventListener("error", onDone, { once: true });
    });

    setTimeout(resolve, MAX_IMAGE_WAIT_MS);
  });
}

export function UIProvider({ children }) {
  const [loading, setLoadingState] = useState(false);
  const [message, setMessage] = useState(undefined);
  const [backgroundImg, setBackgroundImg] = useState(undefined);
  const [userTheme, setUserTheme] = useState(getStoredTheme);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const activeLoadingCountRef = useRef(0);
  const loadingStartedAtRef = useRef(0);
  const hideRequestIdRef = useRef(0);
  const loadingStateRef = useRef(false);

  const updateLoadingState = useCallback((nextLoadingState) => {
    loadingStateRef.current = nextLoadingState;
    setLoadingState(nextLoadingState);
  }, []);

  const setLoading = useCallback((isLoading) => {
    if (isLoading) {
      activeLoadingCountRef.current += 1;
      hideRequestIdRef.current += 1;

      if (!loadingStateRef.current) {
        loadingStartedAtRef.current = Date.now();
        updateLoadingState(true);
      }

      return;
    }

    activeLoadingCountRef.current = Math.max(0, activeLoadingCountRef.current - 1);

    if (activeLoadingCountRef.current > 0) {
      return;
    }

    const requestId = hideRequestIdRef.current + 1;
    hideRequestIdRef.current = requestId;

    const hideOverlay = async () => {
      await waitForNextPaint();
      await waitForImages();

      const elapsed = Date.now() - loadingStartedAtRef.current;
      if (elapsed < MIN_LOADING_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed));
      }

      if (hideRequestIdRef.current !== requestId || activeLoadingCountRef.current > 0) {
        return;
      }

      updateLoadingState(false);
    };

    hideOverlay();
  }, [updateLoadingState]);

  const effectiveTheme = getEffectiveTheme(userTheme, systemTheme);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const onThemeChange = (event) => {
      setSystemTheme(event.matches ? Theme.DARK : Theme.LIGHT);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onThemeChange);
      return () => mediaQuery.removeEventListener('change', onThemeChange);
    }

    mediaQuery.addListener(onThemeChange);
    return () => mediaQuery.removeListener(onThemeChange);
  }, []);

  useEffect(() => {
    const themeValue = effectiveTheme === Theme.DARK ? 'dark' : 'light';
    const resolvedThemeValue = effectiveTheme === Theme.DARK ? Theme.DARK : Theme.LIGHT;

    document.documentElement.setAttribute('data-theme', themeValue);
    document.documentElement.setAttribute('data-theme-resolved', resolvedThemeValue);
    document.body.setAttribute('data-theme', themeValue);
    document.body.setAttribute('data-theme-resolved', resolvedThemeValue);
  }, [effectiveTheme]);

  useEffect(() => {
    if (!userTheme || typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(THEME_STORAGE_KEY, userTheme);
    } catch {
      // Ignore storage failures to avoid breaking UI behavior.
    }
  }, [userTheme]);

  return (
    <UIContext.Provider value={{
      loading,
      setLoading,
      message,
      setMessage,
      backgroundImg,
      setBackgroundImg,
      userTheme,
      setUserTheme,
      effectiveTheme,
      systemTheme
    }}>
      {children}

      {loading && <Loading />}
      {message && <Message {...message} />}
    </UIContext.Provider>
  );
}

export function useUI() {
  return useContext(UIContext);
}
